import type { SettingValue } from '@palaccount/contracts';

/**
 * ADM-08 — WHERE THE SIX CHOICES ACTUALLY LAND.
 *
 * One function, called from two places: once before first paint from what the
 * last session left in `localStorage`, and again the moment `/api/settings/
 * effective` answers with the person's real, server-stored values.
 *
 * ── WHY LOCAL STORAGE IS STILL HERE, AND WHY IT IS NOT THE TRUTH ───────────
 *
 * BR-ADM-08-03 settles the storage question the prototype left open: "Every
 * setting must survive signing out and must FOLLOW THE PERSON TO ANOTHER
 * COMPUTER… The prototype keeps them in the open browser tab only, so they are
 * lost on every reload." The server row is therefore the truth, and it is
 * `user_settings` — the same table ADM-01's personal overrides already use, so
 * ADM-08 introduced no second mechanism.
 *
 * The local copy is a CACHE with exactly one job: painting the right theme on
 * the first frame. `/api/settings/effective` is a request, and a request takes
 * a moment; without a mirror, a person who chose Dark gets a white flash on
 * every page load. The mirror is written whenever the server answers and read
 * only before it does, so it can never be a second opinion — the moment the
 * server speaks it overwrites the cache.
 *
 * This is also ERR-ADM-08-02's shape: "That change was not saved to your
 * account. It applies on this device until you sign in again." A failed write
 * leaves the choice applied and cached, and the next successful load from
 * another device silently corrects it.
 *
 * And it is EC-15-06's answer for two devices: the LATER server write wins,
 * the other device picks it up on its next page load when
 * `/api/settings/effective` overwrites the cache, and nothing ever tries to
 * sync live.
 *
 * PART 4's personalization rules, at the applier: GEN-171 (remembered per
 * user, follows to another computer — the server row), GEN-172 (text size
 * scales the WHOLE interface — one root font-size, every measurement in
 * rem), GEN-173 (density changes table row padding ONLY — the one
 * `--cell-padding-y` consumer), GEN-174 (personalization never changes a
 * number, a permission or data — the GEN-211 fence: nothing in this file is
 * ever sent to the server as an input).
 *
 * ── WHY THIS IS CSS VARIABLES AND NOT REACT STATE ──────────────────────────
 *
 * BR-ADM-08-01: "Every setting here must apply THE MOMENT IT IS TAPPED, with
 * no Save button and no page reload." Attributes and variables on <html> apply
 * to the whole document in one paint, including screens that are not mounted
 * and components that never re-render. A context would need every consumer to
 * subscribe, and BR-ADM-08-08's "every table in the system" is precisely the
 * case where one missed subscriber is a bug nobody sees.
 */

export const APPEARANCE_KEYS = [
  'appearance.theme',
  'appearance.text_size',
  'appearance.font_style',
  'appearance.font_weight',
  'appearance.density',
  'appearance.accent',
] as const;
export type AppearanceKey = (typeof APPEARANCE_KEYS)[number];

export type Appearance = Record<AppearanceKey, string>;

/** §15's "Empty" row: "a new person gets the defaults". The registry's own. */
export const APPEARANCE_DEFAULTS: Appearance = {
  'appearance.theme': 'light',
  'appearance.text_size': 'default',
  'appearance.font_style': 'jakarta',
  'appearance.font_weight': 'regular',
  'appearance.density': 'cozy',
  'appearance.accent': 'signal_blue',
};

export const APPEARANCE_STORAGE_KEY = 'app.appearance';

/** §5 field 2's four sizes, in the spec's exact pixels. */
const TEXT_SIZE_PX: Record<string, string> = {
  small: '13.5px',
  default: '15px',
  large: '16.5px',
  xl: '18.5px',
};

/** §5 field 4 — Light 300, Regular 400, Medium 500. */
const FONT_WEIGHT: Record<string, string> = {
  light: '300',
  regular: '400',
  medium: '500',
};

/** §5 field 5 — Compact 9, Cozy 14, Comfortable 19 pixels. */
const DENSITY_PX: Record<string, string> = {
  compact: '9px',
  cozy: '14px',
  comfortable: '19px',
};

/**
 * §5 field 3's four families, as CSS font stacks.
 *
 * The first three are self-hosted through @fontsource (imported in main.tsx),
 * so a strict CSP needs no `font-src` exception — the same reasoning that put
 * the Arabic face there. `system` is deliberately NOT a webfont: §5 lists
 * "System UI" as a choice, and the whole value of that choice is that it costs
 * nothing to download and matches the person's own machine.
 */
const FONT_FAMILY: Record<string, string> = {
  // The `Variable` suffix is the family name @fontsource-variable declares, not
  // decoration — the static packages call themselves 'Plus Jakarta Sans' and
  // these call themselves 'Plus Jakarta Sans Variable', and a stack naming the
  // wrong one silently falls through to the next entry.
  jakarta: "'Plus Jakarta Sans Variable'",
  plex: "'IBM Plex Sans Variable'",
  source_serif: "'Source Serif 4 Variable'",
  system: 'system-ui',
};

/**
 * Applies all six to the document. Idempotent, and safe to call on every
 * change — the browser coalesces the paint.
 */
export function applyAppearance(appearance: Appearance): void {
  const root = document.documentElement;

  // §5 field 1 — exactly Light and Dark (BR-ADM-08-05). The `dark` class is
  // the same one the top bar's theme button has always toggled; ADM-08 did not
  // introduce a second theming mechanism, it gave the existing one a home and
  // a server row.
  root.classList.toggle('dark', appearance['appearance.theme'] === 'dark');

  root.style.setProperty(
    '--base-font-size',
    TEXT_SIZE_PX[appearance['appearance.text_size']] ?? TEXT_SIZE_PX['default']!,
  );
  root.style.setProperty(
    '--base-font-weight',
    FONT_WEIGHT[appearance['appearance.font_weight']] ?? FONT_WEIGHT['regular']!,
  );
  root.style.setProperty(
    '--cell-padding-y',
    DENSITY_PX[appearance['appearance.density']] ?? DENSITY_PX['cozy']!,
  );
  root.style.setProperty(
    '--font-chosen',
    FONT_FAMILY[appearance['appearance.font_style']] ?? FONT_FAMILY['jakarta']!,
  );
  // An ATTRIBUTE rather than a variable, because index.css needs to select on
  // it twice — once for the hue and once for the dark-mode lift, which a
  // variable cannot express on its own.
  root.setAttribute('data-accent', appearance['appearance.accent']);

  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    /* private mode: the choice still applies, it just will not survive a reload */
  }
}

/** The cached copy, for the first frame. Never trusted once the server answers. */
export function readCachedAppearance(): Appearance {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Appearance>;
      return { ...APPEARANCE_DEFAULTS, ...pickKnown(parsed) };
    }
  } catch {
    /* fall through to the defaults */
  }
  return { ...APPEARANCE_DEFAULTS };
}

/**
 * Pulls the six out of `/api/settings/effective`, which returns the whole map.
 * Anything missing or of the wrong shape falls back to its default rather than
 * reaching a stylesheet — a stray value here is a broken page, not a bad
 * number.
 */
export function appearanceFromSettings(
  settings: Record<string, SettingValue> | undefined,
): Appearance {
  if (!settings) return { ...APPEARANCE_DEFAULTS };
  return { ...APPEARANCE_DEFAULTS, ...pickKnown(settings) };
}

function pickKnown(source: Record<string, unknown>): Partial<Appearance> {
  const out: Partial<Appearance> = {};
  for (const key of APPEARANCE_KEYS) {
    const value = source[key];
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

/**
 * IT-L5 — "Show images" on the Items list.
 *
 * A per-DEVICE convenience in `localStorage`, deliberately NOT an ADM-08 row:
 * the six appearance choices are the registry's and each needs a seeded
 * setting row; whether a 40px thumbnail column is drawn changes no number,
 * no permission and no data (GEN-174), and a person who hides it on the shop
 * computer has no reason to want it hidden on their laptop. Same try/catch
 * shape as the appearance cache — private mode just forgets the choice.
 */
export const ITEM_IMAGES_STORAGE_KEY = 'app.items.show_images';

export function readShowItemImages(): boolean {
  try {
    return localStorage.getItem(ITEM_IMAGES_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function writeShowItemImages(show: boolean): void {
  try {
    localStorage.setItem(ITEM_IMAGES_STORAGE_KEY, show ? 'true' : 'false');
  } catch {
    /* private mode: the column still toggles, it just will not survive a reload */
  }
}

/**
 * The side menu's pin — "Keep the menu open" on the desktop rail
 * (`app/shell/sidebar.tsx`).
 *
 * Per DEVICE, like IT-L5 above and for the same reason: whether the rail
 * rests open or closed is a question about THIS window's width and THIS
 * pointer, not about the person — a 13-inch laptop wants the icons, a
 * 27-inch monitor can afford the labels. It changes no number, no permission
 * and no data (GEN-174), so it is not an ADM-08 row. Default: collapsed,
 * which is the rail's whole point; the choice to keep it open is the one
 * worth remembering.
 */
export const NAV_PINNED_STORAGE_KEY = 'app.nav.pinned';

export function readNavPinned(): boolean {
  try {
    return localStorage.getItem(NAV_PINNED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeNavPinned(pinned: boolean): void {
  try {
    localStorage.setItem(NAV_PINNED_STORAGE_KEY, pinned ? 'true' : 'false');
  } catch {
    /* private mode: the rail still pins for this page, it just will not survive a reload */
  }
}
